import { isMobile } from "react-device-detect";
/**
 * Class for easily handling feature-dragging Listeners.
 * Works with Points and Symbols!
 *
 * How to use:
 *
 * (easy mode: use the setHandlerToMap function.., or return the handleFunction -> read below how:)
 *
 * 1. Instantiate an object
 *
 * 2. Get the handleDrag function
 *
 * 3. listen to onMove/onStart/onRelease -> update your FeatureCollection that consist of the drag items!
 *
 * Expample:
 *
 * const handleMove =(feature, e)=> {
 *  const movingCollection = featureCollection([feature])
 *  setMovingFeatureCollection(movingCollection)
 *  // -> set this FeatureCollection to one data source
 * }
 *
 * const featureDragger2 = new FeatureDragger(map, {onMove: handleMove})
 *
 * cuMap.on("mousedown", "beaconCircles", featureDragger2.handleDrag)
 */
export class FeatureDragger {
    constructor(map, options) {
        this.draggingFeature = null;
        this.map = map;
        if (options.onRelease) {
            this.onRelease = options.onRelease;
        }
        if (options.onMove) {
            this.onMove = options.onMove;
        }
        if (options.onStart) {
            this.onStart = options.onStart;
        }
        if (options.layerId) {
            this.layerId = options.layerId;
        }
    }
    _handleDrag(event) {
        const onMove = (e) => {
            var _a;
            // ----- User is dragging beacon ----
            const coords = e.lngLat;
            if (coords) {
                if (this.draggingFeature) {
                    this.draggingFeature = {
                        ...this.draggingFeature,
                        toJSON: () => { },
                        geometry: {
                            ...(_a = this.draggingFeature) === null || _a === void 0 ? void 0 : _a.geometry,
                            type: "Point",
                            coordinates: [coords.lng, coords.lat],
                        },
                    };
                }
                if (this.onMove) {
                    this.onMove(this.draggingFeature, e);
                }
            }
        };
        const onUp = (e) => {
            // ---- User lets beacon go ----
            // clear coldDrag in case user just clicked instead of dragged
            // Reset everything, as if nothing ever happened
            if (this.onRelease) {
                this.onRelease(this.draggingFeature, e);
            }
            this.draggingFeature = null;
            this.map.off(isMobile ? "touchmove" : "mousemove", onMove);
        };
        const coldDragBlock = () => {
            clearTimeout(this.coldDragBlocker);
        };
        event.preventDefault();
        if (!this.map)
            return;
        // ----- User grabs beacon ----
        // important to call this here, to prevent clicks from being considered
        // (it resets the coldDragTimeout)
        this.map.once(isMobile ? "touchend" : "mouseup", coldDragBlock);
        const features = event.features;
        // only if user lets go the timer gets cleared!
        // so fast taps will not be recognized as drags
        if (features && features[0]) {
            const feature = features[0];
            // @ts-ignore
            this.coldDragBlocker = setTimeout(() => {
                this.draggingFeature = feature;
                if (this.onStart) {
                    this.onStart(this.draggingFeature, event);
                }
                this.map.once(isMobile ? "touchend" : "mouseup", onUp);
                // this.map.setFeatureState(feature, { hide: true });
                this.map.on(isMobile ? "touchmove" : "mousemove", onMove);
            }, 150);
        }
    }
    /**
     *
     * Return this function to the .on( "touchstart" / "mousedown"...) function of CUMap (also works with normal MapLibre/Mapbox)
     *
     * - Example for mobile devices:
     *
     * const featureDragger1 = new FeatureDragger (....)
     *
     * cuMap.on("touchstart", "beaconCircles", featureDragger1.handleDrag)
     *
     * - Example for desktop devices:
     *
     * const featureDragger2 = new FeatureDragger(...)
     *
     * cuMap.on("mousedown", "beaconCircles", featureDragger2.handleDrag)
     */
    get handleDrag() {
        return this._handleDrag.bind(this);
    }
    /**
     * Set the handler to the map.
     */
    setHandlerToMap() {
        if (!this.layerId) {
            console.warn("FeatureDrag could not set a handler, because no layerId was given in the constructor");
            return;
        }
        this.unsetHandlerFromMap();
        this.map.on(isMobile ? "touchstart" : "mousedown", this.layerId, this.handleDrag);
    }
    /** Unset handler from map */
    unsetHandlerFromMap() {
        if (!this.layerId) {
            console.warn("FeatureDrag could not unset a handler, because no layerId was given in the constructor");
            return;
        }
        this.map.off(isMobile ? "touchstart" : "mousedown", this.layerId, this.handleDrag);
    }
}
